import React, { useEffect, useMemo, useState } from 'react';
import { Column, Row, SpaceBetweenRow } from '@shared/ui/atoms/StructuralLayout';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { Switch } from '@shared/ui/atoms/Switch';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  loadConfig,
  selectConfig,
  selectHasGlobalConfigLoaded,
  setPlatformConfig
} from "@shared/store/config.duck";
import { Accordian } from '@shared/animations/AnimationVariants';
import TextField from '@shared/ui/atoms/TextField';
import moment from "moment";
import { capitalizeFirstLetter } from "@shared/utils/ArrayUtils";
import { StartDateDropDown } from "./HolidayYearSetting";
import styled from "styled-components";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { isNumber } from "@shared/utils/TypeUtils";
import { updateConfigProperty } from "@shared/services/ConfigService";
import { successNotification } from "@shared/store/notification.duck";

export function LeaveCarryOverSettings(props: Props) {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [carryOverDays, setCarryOverDays] = useState<undefined | number>();
  const [hasExpiryRules, setHasExpiryRules] = useState(false);
  const [expiryMonth, setExpiryMonth] = useState<undefined | number>();
  const [expiryDay, setExpiryDay] = useState<undefined | number>();
  const hasGlobalConfigLoaded = useSelector(selectHasGlobalConfigLoaded);
  const config = useSelector(selectConfig);
  const dispatch = useDispatch();

  const MONTHS = useMemo(() => {
    const date = moment(0);
    return [
      { name: capitalizeFirstLetter(date.format('MMMM')), id: 1 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 2 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 3 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 4 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 5 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 6 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 7 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 8 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 9 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 10 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 11 },
      { name: capitalizeFirstLetter(date.month(date.month() + 1).format('MMMM')), id: 12 },
    ]
  }, []);

  const DAYS = useMemo(() => {
    let arr = [];
    for (let i = 1; i < 32; i++) {
      arr.push({ name: i.toString(), id: i })
    }
    return arr;
  }, []);

  useEffect(() => {
    if (hasGlobalConfigLoaded) {
      if (config.autoCarryOverDaysLimit) {
        setCarryOverDays(config.autoCarryOverDaysLimit)
      }
      if (config.autoCarryOverExpiryMonth && config.autoCarryOverExpiryDay) {
        setHasExpiryRules(true);
        setExpiryMonth(config.autoCarryOverExpiryMonth);
        setExpiryDay(config.autoCarryOverExpiryDay);
      }
    }
  }, [config, hasGlobalConfigLoaded]);


  const onEnabledChange = async (enabled: boolean) => {
    try {
      setIsLoading(true);
      dispatch(setPlatformConfig({...config, autoCarryOverEnabled: enabled}));
      await updateConfigProperty('carryOverEnabled', enabled)
    } finally {
      setIsLoading(false);
    }
  }

  const onMaxCarryOverDaysChange = async (value: string) => {
    if (isNumber(value)) {
      dispatch(setPlatformConfig({...config, autoCarryOverDaysLimit: parseInt(value)}));
    }
  }

  const saveMaxCarryOverDays = async () => {
    try {
      setIsLoading(true);
      if (config.autoCarryOverDaysLimit) {
        await updateConfigProperty('carryOverDaysLimit', config.autoCarryOverDaysLimit)
      }
    } finally {
      setIsLoading(false);
    }
  }

  const setExpiryRules = async (value: boolean) => {
    setHasExpiryRules(value);
    if (!value) {
      // clear expiry date
      await onExpiryMonthChange(undefined);
      await onExpiryDayOfMonthChange(undefined);
    }
  }

  const onExpiryMonthChange = async (month?: number) => {
    try {
      setIsLoading(true);
      setExpiryMonth(month);
      if (month) {
        await updateConfigProperty('carryOverExpiryMonth', month);
        dispatch(successNotification('Expiry date set'));
        dispatch(loadConfig());
      } else {
        await updateConfigProperty('carryOverExpiryMonth', null);
        dispatch(loadConfig());
        dispatch(successNotification('Expiry date cleared'));
      }
    } finally {
      setIsLoading(false);
    }
  }

  const onExpiryDayOfMonthChange = async (dayOfMonth?: number) => {
    try {
      setIsLoading(true);
      setExpiryDay(dayOfMonth);
      if (dayOfMonth) {
        await updateConfigProperty('carryOverExpiryDayOfMonth', dayOfMonth);
        dispatch(successNotification('Expiry date set'));
        dispatch(loadConfig());
      } else {
        await updateConfigProperty('carryOverExpiryDayOfMonth', null);
        dispatch(loadConfig());
        dispatch(successNotification('Expiry date cleared'));
      }
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.carry-over.title')}</BodyLarge>
        <Switch disabled={isLoading}
                value={!!config.autoCarryOverEnabled}
                onChange={enabled => onEnabledChange(enabled)}/>
      </SpaceBetweenRow>
      <BodyRegular>{t('settings.carry-over.description')}</BodyRegular>

      <Accordian isOpen={!!config.autoCarryOverEnabled}>
        <BodySmall weight={600} style={{marginTop: 16, marginBottom: 4}}>{t('settings.carry-over.carry-over-days')}</BodySmall>
        <TextField onChange={onMaxCarryOverDaysChange} onBlur={saveMaxCarryOverDays}
                   value={(carryOverDays ?? '').toString()}
                   disabled={isLoading}
                   rightText={'days'} />

        <SpaceBetweenRow>
          <BodyLarge colour={Colours.blue}>{t('settings.carry-over.rules-title')}</BodyLarge>
          <Switch disabled={isLoading}
                  value={hasExpiryRules}
                  onChange={enabled => setExpiryRules(enabled)}/>
        </SpaceBetweenRow>
        <BodyRegular style={{marginTop: 0, marginBottom: 16}}>{t('settings.carry-over.rules-description')}</BodyRegular>

        <Accordian isOpen={hasExpiryRules}>
          <BodyRegular weight={600} style={{marginTop: 0, marginBottom: 12}}>{t('settings.carry-over.expires-on')}</BodyRegular>

          <ExpiryDateRow>
            <Column style={{marginRight: 8}}>
              <BodyRegular weight={600}>{t('settings-page.edit-user.birthday.day')}</BodyRegular>
              <StartDateDropDown items={DAYS}
                                 onSelect={(v: any) => onExpiryDayOfMonthChange(parseInt(v))}
                                 selectedId={expiryDay}
                                 placeholder="settings-page.edit-user.birthday.day" />
            </Column>
            <Column>
              <BodyRegular weight={600}>{t('settings-page.edit-user.birthday.month')}</BodyRegular>
              <StartDateDropDown items={MONTHS}
                                 onSelect={(v: any) => onExpiryMonthChange(parseInt(v))}
                                 selectedId={expiryMonth}
                                 placeholder="textinput.select-month" />
            </Column>
          </ExpiryDateRow>
        </Accordian>
      </Accordian>
    </>
  )
}

interface Props {
}

const ExpiryDateRow = styled(Row)`
  flex-direction: column;
  @media(${tablet}) {
    flex-direction: row;
  }
  p {
    margin-left: 8px;
    margin-bottom: 4px;
  }
`
